import React from "react";
import { ReactElement } from "react";
import {
  NieuweLastenOverzichtHoofdstuk,
  Lasten,
  LastenOverzichtLasten,
} from "../../../../.generated/rapportage/rapportagetypes";
import {
  Paragraph,
  renderTextWithLineBreaks,
} from "../../components/Paragraph";

import Table from "../../components/Table";
import { LineChart } from "../../components/Chart";
import { ChartDataset } from "chart.js";
import { Column } from "../../components/BaseTable";
import Heading from "../../components/Heading";
import { Chapter } from "../../components/Chapter";
import defaultTheme from "../../../../styles/themes/default";

/* istanbul ignore next */
export const hasLastenVoordelen = (
  lastenSituatie: LastenOverzichtLasten | null | undefined
): boolean => {
  if (!lastenSituatie) {
    return false;
  }

  const fiscaalNadelen = lastenSituatie.fiscaalNadelen ?? [];
  const fiscaalVoordelen = lastenSituatie.fiscaalVoordelen ?? [];
  const nettoLasten = lastenSituatie.nettoLasten ?? [];

  const sum1 = fiscaalNadelen.reduce((a, b) => a + b, 0);
  const sum2 = fiscaalVoordelen.reduce((a, b) => a + b, 0);
  const sum3 = nettoLasten.reduce((a, b) => a + b, 0);

  const total = sum1 + sum2 + sum3;

  if (fiscaalNadelen.length || fiscaalVoordelen.length || nettoLasten.length) {
    return total > 0;
  }

  return false;
};

// Helper function to generate chart datasets from lasten data
export const getDataset = (
  data: LastenOverzichtLasten
): ChartDataset<"line">[] => {
  const datasets: ChartDataset<"line">[] = [];

  // Check if we have valid data
  if (
    !data ||
    (!data.nettoLasten && !data.fiscaalVoordelen && !data.fiscaalNadelen)
  ) {
    // Return empty datasets to make the chart visible but with no data
    return [
      {
        label: "Netto last",
        fill: true,
        data: [],
        pointStyle: "triangle",
        pointRadius: 4,
        borderColor: defaultTheme.colors.chartBlue,
        backgroundColor: defaultTheme.colors.chartBlueLight,
      },
      {
        label: "Bruto last",
        data: [],
        pointStyle: "triangle",
        pointRadius: 4,
        borderColor: "rgb(139, 207, 94)",
        backgroundColor: "rgba(255, 204, 0, 0.5)",
      },
    ];
  }

  /* istanbul ignore next */
  if (data.nettoLasten && data.nettoLasten.length > 0) {
    datasets.push({
      label: "Netto last",
      fill: true,
      data: data.nettoLasten,
      pointStyle: "triangle",
      pointRadius: 4,
      borderColor: defaultTheme.colors.chartGreen,
      backgroundColor: "rgba(124, 145, 64, 0.5)",
    });
  } else {
    datasets.push({
      label: "Netto last",
      fill: true,
      data: [],
      pointStyle: "triangle",
      pointRadius: 4,
      borderColor: defaultTheme.colors.chartGreen,
      backgroundColor: "rgba(124, 145, 64, 0.5)",
    });
  }

  // Combine fiscaalVoordelen and fiscaalNadelen into a single "Fiscaal" dataset
  if (
    (data.fiscaalVoordelen && data.fiscaalVoordelen.length > 0) ||
    (data.fiscaalNadelen && data.fiscaalNadelen.length > 0) ||
    (data.nettoLasten && data.nettoLasten.length > 0)
  ) {
    const fiscaalVoordelen =
      data.fiscaalVoordelen || /* istanbul ignore next */ [];
    const fiscaalNadelen = data.fiscaalNadelen || /* istanbul ignore next */ [];
    const nettoLasten = data.nettoLasten || /* istanbul ignore next */ [];

    // Determine the maximum length for the combined array
    const maxLength = Math.max(
      fiscaalVoordelen.length,
      fiscaalNadelen.length,
      nettoLasten.length
    );

    // Create combined data array
    const combinedData: number[] = [];

    for (let i = 0; i < maxLength; i++) {
      const voordeel =
        i < fiscaalVoordelen.length
          ? fiscaalVoordelen[i]
          : /* istanbul ignore next */ 0;
      const nadeel =
        i < fiscaalNadelen.length
          ? fiscaalNadelen[i]
          : /* istanbul ignore next */ 0;
      const nettoLast =
        i < nettoLasten.length ? nettoLasten[i] : /* istanbul ignore next */ 0;
      // Combine fiscal values with nettoLasten
      combinedData.push(voordeel - nadeel + nettoLast);
    }

    datasets.push({
      label: "Bruto last",
      data: combinedData,
      borderColor: defaultTheme.colors.chartBlue,
      backgroundColor: defaultTheme.colors.chartBlueLight,
      pointRadius: 4,
    });
  } else {
    datasets.push({
      label: "Bruto last",
      data: [],
      borderColor: defaultTheme.colors.chartBlue,
      backgroundColor: defaultTheme.colors.chartBlueLight,
      pointRadius: 4,
    });
  }

  return datasets;
};

type NieuweLastenProps = {
  nieuweLasten: NieuweLastenOverzichtHoofdstuk;
};

const leeftijd = (row: Lasten): string => {
  let leeftijden = [row.leeftijdAanvrager, row.leeftijdPartner];

  leeftijden = leeftijden.filter((leeftijd) => (leeftijd ?? 0) > 0);

  return leeftijden.join(" / ");
};

export const NieuweLastenChapter = ({
  nieuweLasten,
}: NieuweLastenProps): ReactElement => {
  const columns: Column<Lasten>[] = [
    {
      header: "Jaar",
      accessor: "jaar",
      align: "right",
    },
    {
      header: "Leeftijd",
      accessor: (row: Lasten) => leeftijd(row),
      align: "center",
    },
    {
      header: "Bruto inkomen",
      accessor: "brutoInkomen",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Waarde­opbouw",
      accessor: "waardeopbouw",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Hypo­theek",
      accessor: "somHypotheken",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Rente",
      accessor: "rente",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Aflossing",
      accessor: "hypotheekAflossing",
      align: "right",
      dataType: "currency",
    },
    // {
    //   header: "Uitkering kapitaal­verz.",
    //   accessor: "uitkeringKapitaalverzekeringen",
    //   align: "right",
    //   dataType: "currency",
    // },
    {
      header: "Premies",
      accessor: "verzekeringspremies",
      align: "right",
      dataType: "currency",
    },
    // {
    //   header: "Spaar en beleggings­depots",
    //   accessor: "spaarEnBeleggingsdepots",
    //   align: "right",
    //   dataType: "currency",
    // },
    {
      header: "Opname uit depot",
      accessor: "opnameUitDepot",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Krediet­last",
      accessor: "kredietlast",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Overige uitgaven",
      accessor: "overigeUitgaven",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Huur",
      accessor: "huur",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Bruto last",
      accessor: "brutoLast",
      align: "right",
      dataType: "currency",
      highlight: true,
    },
    {
      header: "Fiscaal voordeel",
      accessor: "fiscaalVoordeel",
      align: "right",
      dataType: "currency",
      highlight: true,
    },
    {
      header: "Belasting box 3",
      accessor: "vrh",
      align: "right",
      dataType: "currency",
    },
    {
      header: "Netto last",
      accessor: "nettoLast",
      align: "right",
      dataType: "currency",
      highlight: true,
    },
  ];

  // Extract boolean conditions to variables
  const hasHuidigeSituatie = hasLastenVoordelen(
    nieuweLasten.lastenOverzicht?.grafieken?.huidigeSituatie
  );

  const hasNieuweSituatie = hasLastenVoordelen(
    nieuweLasten.lastenOverzicht?.grafieken?.nieuweSituatie
  );

  return (
    <Chapter>
      {nieuweLasten.titel && <Heading level={2} text={`${nieuweLasten.titel}`}></Heading>}

      {nieuweLasten?.paragrafen?.["p3050702"] && (
        <>
          <Paragraph className="chapter__paragraph" whiteSpace="">
            {renderTextWithLineBreaks(
              nieuweLasten?.paragrafen?.["p3050702"]?.inhoud ?? ""
            )}
          </Paragraph>
        </>
      )}

      {nieuweLasten.lastenOverzicht?.jaarGegevens && (
        <Table
          compact={true}
          columns={columns}
          data={
            [
              ...nieuweLasten.lastenOverzicht.jaarGegevens,
              {
                ...nieuweLasten.lastenOverzicht.totalen,
                somHypotheken: null,
                waardeopbouw: null,
                brutoInkomen:null,
                jaar: "Totaal",
              },
            ] as Lasten[]
          }
          includeFooter={true}
          id="nieuwe-lasten-table"
        />
      )}

      {/* na de maandlasten tabel, altijd een break, aangezien dit vrijwel altijd een volle pagina is */}
      <div data-break-before="always" />

      {/* Chart for huidige situatie */
      hasHuidigeSituatie && (
        <>
          <br />
          <Paragraph paragraaf={nieuweLasten?.paragrafen?.["p3050703"]} />
          <LineChart
            title="Huidige situatie"
            labels={(
              nieuweLasten.lastenOverzicht?.grafieken?.huidigeSituatie?.jaren ||
              []
            ).map((jaar) => jaar?.toString() || /* istanbul ignore next */ "")}
            datasets={getDataset(
              nieuweLasten.lastenOverzicht?.grafieken
                ?.huidigeSituatie as LastenOverzichtLasten
            )}
          />
        </>
      )}

      {/* Chart for toekomstige situatie */}
      {hasNieuweSituatie && (
        <>
          <Paragraph paragraaf={nieuweLasten?.paragrafen?.["p305070301"]} />
          <LineChart
            title="Toekomstige situatie"
            labels={(
              nieuweLasten.lastenOverzicht?.grafieken?.nieuweSituatie?.jaren ||
              []
            ).map((jaar) => jaar?.toString() || /* istanbul ignore next */ "")}
            datasets={getDataset(
              nieuweLasten.lastenOverzicht?.grafieken
                ?.nieuweSituatie as LastenOverzichtLasten
            )}
          />
        </>
      )}
    </Chapter>
  );
};
